import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Region } from '../types';
import { fetchRegions } from '../api/fetchBars';

interface RegionContextType {
  regions: Region[];
  currentRegion: Region | null;
  isAuto: boolean;
  isLoading: boolean;
  error: string | null;
  setCurrentRegion: (region: Region, isAuto?: boolean) => void;
  getRegionBySlug: (slug: string) => Region | null;
}

const RegionContext = createContext<RegionContextType | null>(null);

export function RegionProvider({ children }: { children: React.ReactNode }) {
  const [regions, setRegions] = useState<Region[]>([]);
  const [currentRegion, setCurrentRegionState] = useState<Region | null>(null);
  const [isAuto, setIsAuto] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch regions on mount
  useEffect(() => {
    async function loadRegions() {
      try {
        const data = await fetchRegions();
        setRegions(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load regions:', err);
        setError('Failed to load regions');
      } finally {
        setIsLoading(false);
      }
    }
    loadRegions();
  }, []);

  const setCurrentRegion = useCallback((region: Region, auto = false) => {
    setCurrentRegionState(region);
    setIsAuto(auto);
  }, []);

  const getRegionBySlug = useCallback((slug: string): Region | null => {
    return regions.find((r) => r.slug === slug) || null;
  }, [regions]);

  return (
    <RegionContext.Provider
      value={{
        regions,
        currentRegion,
        isAuto,
        isLoading,
        error,
        setCurrentRegion,
        getRegionBySlug,
      }}
    >
      {children}
    </RegionContext.Provider>
  );
}

export function useRegion() {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
}
